import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';

interface LineItem {
  product_id: number;
  product_name: string;
  quantity: number;
  is_weight_based: boolean;
  unit_price: number;
  line_total: number;
  is_red_bridge: boolean;
}

interface Transaction {
  id: number;
  date: string;
  amount: string;
  payment_method: string;
  invoice_number: string | null;
  helcim_transaction_id: string;
  pos_transaction_id: number | null;
  cashier: string | null;
  items: LineItem[];
}

interface PromotedMember {
  id: number;
  name: string;
  email: string;
}

// Shape returned by /pos/customers/?q= for an existing local member row.
interface MemberSearchResult {
  id: number;
  name: string;
  member_code?: string;
  phone?: string;
  is_unmatched?: boolean;
}

interface TopProduct {
  product_name: string;
  total_quantity: number;
  total_spend: number;
  visits: number;
}

interface ShadowPoints {
  points: number;
  rewards_available: number;
  credit_value_dollars: number;
  invite_priority: boolean;
}

interface WalkInDetail {
  id: number;
  cardholder_name: string;
  card_last_four: string;
  card_type: string;
  email: string;
  phone: string;
  visit_count: number;
  total_spend: string;
  avg_ticket: string | null;
  first_visit: string | null;
  last_visit: string | null;
  invite_sent_at: string | null;
  invite_email: string | null;
  promoted_to_member: PromotedMember | null;
  promoted_at: string | null;
  transactions: Transaction[];
  top_products?: TopProduct[];
  shadow_points?: ShadowPoints;
}

const UnmatchedVisitorDetail: React.FC = () => {
  const { visitorId } = useParams<{ visitorId: string }>();
  const navigate = useNavigate();
  const [visitor, setVisitor] = useState<WalkInDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteResult, setInviteResult] = useState<string | null>(null);
  // Connect mode: invite (create new) vs link (to an existing member).
  const [connectMode, setConnectMode] = useState<'invite' | 'link'>('invite');
  // Server search: input binds to `memberQuery` (instant); the debounced value (400ms)
  // drives the member lookup below, so typing fires one request after the pause.
  const { value: memberQuery, setValue: setMemberQuery, debounced: debouncedMemberQuery } =
    useDebouncedSearch('', 400);
  const [memberResults, setMemberResults] = useState<MemberSearchResult[]>([]);
  const [memberSearching, setMemberSearching] = useState(false);
  const [selectedMember, setSelectedMember] = useState<MemberSearchResult | null>(null);
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [linkResult, setLinkResult] = useState<string | null>(null);
  // Per-transaction-row expansion state for line-item drilldown.
  const [expandedTxns, setExpandedTxns] = useState<Set<number>>(new Set());

  const toggleTxn = (txnId: number) => {
    setExpandedTxns((prev) => {
      const next = new Set(prev);
      if (next.has(txnId)) {
        next.delete(txnId);
      } else {
        next.add(txnId);
      }
      return next;
    });
  };

  const fmtCurrency = (v: number) => `$${v.toFixed(2)}`;

  const load = async () => {
    if (!visitorId) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/bridge-club/walk-ins/${visitorId}/`);
      setVisitor(res.data);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load walk-in detail');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [visitorId]);

  // Send Bridge Club invite for this walk-in. Reuses the POS-staff
  // invite endpoint (it doesn't require a POS transaction context).
  const handleSendInvite = async () => {
    if (!visitor || !inviteEmail.trim()) return;
    setInviteSubmitting(true);
    setInviteResult(null);
    try {
      await api.post('/pos/customers/invite-to-bridge-club/', {
        unmatched_visitor_id: visitor.id,
        email: inviteEmail.trim(),
      });
      setInviteResult(`Invite sent to ${inviteEmail.trim()}`);
      setInviteEmail('');
      load();
    } catch (err: any) {
      setInviteResult(
        err?.response?.data?.error || 'Failed to send invite'
      );
    } finally {
      setInviteSubmitting(false);
    }
  };

  // Search existing local Bridge Club members to link this walk-in to.
  // Reuses the POS staff search endpoint; we drop is_unmatched rows so
  // staff can't "link" one walk-in to another walk-in.
  // Runs 400ms after typing stops. Preserves the min-2-char guard so we don't query
  // the endpoint on a single character (unchanged behavior, just debounced).
  useEffect(() => {
    const query = debouncedMemberQuery.trim();
    setSelectedMember(null);
    if (query.length < 2) {
      setMemberResults([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setMemberSearching(true);
        const res = await api.get(`/pos/customers/?q=${encodeURIComponent(query)}`);
        const members: MemberSearchResult[] = (res.data?.members || []).filter(
          (m: MemberSearchResult) => !m.is_unmatched && m.id != null
        );
        if (!cancelled) setMemberResults(members);
      } catch {
        if (!cancelled) setMemberResults([]);
      } finally {
        if (!cancelled) setMemberSearching(false);
      }
    })();
    return () => { cancelled = true; };
  }, [debouncedMemberQuery]);

  // Link this walk-in's spend/points to the selected existing member.
  const handleLinkToMember = async () => {
    if (!visitor || !selectedMember) return;
    setLinkSubmitting(true);
    setLinkResult(null);
    try {
      const res = await api.post(`/pos/walkins/${visitor.id}/link-to-member/`, {
        member_id: selectedMember.id,
      });
      setLinkResult(res.data?.message || `Linked to ${selectedMember.name}`);
      setMemberQuery('');
      setMemberResults([]);
      setSelectedMember(null);
      load();
    } catch (err: any) {
      setLinkResult(err?.response?.data?.error || 'Failed to link walk-in');
    } finally {
      setLinkSubmitting(false);
    }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading walk-in details…</div>;
  if (error || !visitor) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ color: '#B91C1C' }}>{error || 'Not found'}</div>
        <button onClick={() => navigate('/admin/bridge-club')} style={{ marginTop: 12 }}>
          Back
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 980, margin: '0 auto' }}>
      <button
        onClick={() => navigate('/admin/bridge-club')}
        style={{ marginBottom: 16, background: 'transparent', border: 'none', color: '#2D5A27', cursor: 'pointer' }}
      >
        ← Back to Bridge Club
      </button>

      <h1 style={{ marginBottom: 4 }}>{visitor.cardholder_name}</h1>
      <div style={{ color: '#6B7280', marginBottom: 24 }}>
        Walk-in customer • {visitor.card_type ? `${visitor.card_type} ` : ''}
        {visitor.card_last_four ? `···· ${visitor.card_last_four}` : '(no card on file)'}
      </div>

      {/* Profile summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#6B7280' }}>Total spend</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#166534' }}>${visitor.total_spend}</div>
        </div>
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#6B7280' }}>Visits</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{visitor.visit_count}</div>
        </div>
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#6B7280' }}>Avg ticket</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>${visitor.avg_ticket || '0.00'}</div>
        </div>
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: '#6B7280' }}>First / last visit</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {visitor.first_visit || '—'} → {visitor.last_visit || '—'}
          </div>
        </div>
      </div>

      {/* Invite / promotion section */}
      <div style={{ background: '#FFFBEB', border: '1px solid #F59E0B', borderRadius: 8, padding: 16, marginBottom: 24 }}>
        {visitor.promoted_to_member ? (
          <div>
            <div style={{ fontWeight: 600, color: '#065F46', marginBottom: 4 }}>
              Joined Bridge Club
            </div>
            <div style={{ color: '#1F2937', fontSize: 14 }}>
              {visitor.promoted_to_member.name} ({visitor.promoted_to_member.email})
              {visitor.promoted_at && (
                <span style={{ color: '#6B7280' }}> · {new Date(visitor.promoted_at).toLocaleDateString()}</span>
              )}
            </div>
            <Link
              to={`/admin/bridge-club/members/${visitor.promoted_to_member.id}`}
              style={{ display: 'inline-block', marginTop: 8, color: '#2D5A27', textDecoration: 'underline' }}
            >
              Open member profile →
            </Link>
          </div>
        ) : (
          <>
            {/* Choose how to connect this walk-in to Bridge Club:
                create a new account (invite) or link to an existing one. */}
            <div role="radiogroup" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, color: '#374151' }}>
                <input type="radio" name="connectMode" checked={connectMode === 'invite'} onChange={() => setConnectMode('invite')} />
                Send invite to create a new account
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, color: '#374151' }}>
                <input type="radio" name="connectMode" checked={connectMode === 'link'} onChange={() => setConnectMode('link')} />
                Link to an existing member
              </label>
            </div>
            {connectMode === 'invite' ? (
              visitor.invite_sent_at ? (
          <div>
            <div style={{ fontWeight: 600, color: '#92400E', marginBottom: 4 }}>
              Invite already sent
            </div>
            <div style={{ color: '#78350F', fontSize: 14, marginBottom: 12 }}>
              Sent to {visitor.invite_email} on {new Date(visitor.invite_sent_at).toLocaleDateString()}.
              Not yet accepted.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="Resend to (email address)"
                style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #D97706' }}
              />
              <button
                onClick={handleSendInvite}
                disabled={inviteSubmitting || !inviteEmail.trim()}
                style={{ background: '#2D5A27', color: 'white', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}
              >
                {inviteSubmitting ? 'Sending…' : 'Resend invite'}
              </button>
            </div>
            {inviteResult && (
              <div style={{ marginTop: 8, fontSize: 13, color: '#166534' }}>{inviteResult}</div>
            )}
          </div>
        ) : (
          <div>
            <div style={{ fontWeight: 600, color: '#92400E', marginBottom: 4 }}>
              Send a Bridge Club invite?
            </div>
            <div style={{ color: '#78350F', fontSize: 14, marginBottom: 12 }}>
              This walk-in has visited {visitor.visit_count} time{visitor.visit_count === 1 ? '' : 's'} and spent ${visitor.total_spend}.
              Inviting them creates a Bridge Club account with their purchase history pre-loaded.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="Email address"
                style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #D97706' }}
              />
              <button
                onClick={handleSendInvite}
                disabled={inviteSubmitting || !inviteEmail.trim()}
                style={{ background: '#2D5A27', color: 'white', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}
              >
                {inviteSubmitting ? 'Sending…' : 'Send Bridge Club Invite'}
              </button>
            </div>
            {inviteResult && (
              <div style={{ marginTop: 8, fontSize: 13, color: '#166534' }}>{inviteResult}</div>
            )}
          </div>
              )
            ) : (
              <div>
                <div style={{ fontWeight: 600, color: '#1E3A8A', marginBottom: 4 }}>
                  Link to an existing Bridge Club member
                </div>
                <div style={{ color: '#1E40AF', fontSize: 14, marginBottom: 12 }}>
                  Credits this walk-in's ${visitor.total_spend} and {visitor.visit_count} visit
                  {visitor.visit_count === 1 ? '' : 's'} to a member who already has an account
                  (e.g. a spouse sharing the card). No new account is created.
                </div>
                <input
                  type="text"
                  value={memberQuery}
                  onChange={(e) => setMemberQuery(e.target.value)}
                  placeholder="Search members by name, phone, or code"
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #93C5FD', boxSizing: 'border-box' }}
                />
                {memberSearching && (
                  <div style={{ marginTop: 8, fontSize: 13, color: '#6B7280' }}>Searching…</div>
                )}
                {!memberSearching && memberResults.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {memberResults.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => setSelectedMember(m)}
                        style={{
                          textAlign: 'left',
                          padding: '8px 10px',
                          borderRadius: 6,
                          border: selectedMember?.id === m.id ? '2px solid #2563EB' : '1px solid #E5E7EB',
                          background: selectedMember?.id === m.id ? '#EFF6FF' : '#FFFFFF',
                          cursor: 'pointer',
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>{m.name}</span>
                        {m.member_code && <span style={{ color: '#6B7280', marginLeft: 8 }}>{m.member_code}</span>}
                        {m.phone && <span style={{ color: '#9CA3AF', marginLeft: 8 }}>{m.phone}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {!memberSearching && debouncedMemberQuery.trim().length >= 2 && memberResults.length === 0 && (
                  <div style={{ marginTop: 8, fontSize: 13, color: '#6B7280' }}>No matching members.</div>
                )}
                {selectedMember && (
                  <button
                    onClick={handleLinkToMember}
                    disabled={linkSubmitting}
                    style={{ marginTop: 12, background: '#2563EB', color: 'white', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', fontWeight: 600 }}
                  >
                    {linkSubmitting ? 'Linking…' : `Link to ${selectedMember.name}`}
                  </button>
                )}
                {linkResult && (
                  <div style={{ marginTop: 8, fontSize: 13, color: '#166534' }}>{linkResult}</div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Shadow Points card — "what they'd land with if they joined today".
          A concrete invite hook for clerks and admins. */}
      {visitor.shadow_points && (visitor.shadow_points.points > 0 || visitor.shadow_points.invite_priority) && (
        <div style={{
          background: visitor.shadow_points.invite_priority
            ? 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)'
            : '#F9FAFB',
          border: `1px solid ${visitor.shadow_points.invite_priority ? '#10B981' : '#E5E7EB'}`,
          borderRadius: 12,
          padding: 16,
          marginBottom: 24,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#065F46' }}>
              Shadow points
            </div>
            {visitor.shadow_points.invite_priority && (
              <span style={{
                background: '#F59E0B',
                color: 'white',
                fontSize: 11,
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 999,
                textTransform: 'uppercase',
              }}>
                Invite priority
              </span>
            )}
          </div>
          <div style={{ fontSize: 14, color: '#1F2937', lineHeight: 1.5 }}>
            If <strong>{visitor.cardholder_name}</strong> joined today, they'd start with{' '}
            <strong>{visitor.shadow_points.points} points</strong>
            {visitor.shadow_points.rewards_available > 0 ? (
              <> and <strong>${visitor.shadow_points.credit_value_dollars} in store credit</strong> already earned ({visitor.shadow_points.rewards_available} reward{visitor.shadow_points.rewards_available === 1 ? '' : 's'}).</>
            ) : (
              <> ({500 - (visitor.shadow_points.points % 500)} points away from their first $10 reward).</>
            )}
          </div>
        </div>
      )}

      {/* Top products card — only when we have line-item data on file. */}
      {visitor.top_products && visitor.top_products.length > 0 && (
        <>
          <h2 style={{ marginBottom: 12 }}>Top products</h2>
          <div style={{
            background: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: 8,
            marginBottom: 24,
            overflow: 'hidden',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>#</th>
                  <th style={{ padding: 8 }}>Product</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Total qty</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Visits</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Total spend</th>
                </tr>
              </thead>
              <tbody>
                {visitor.top_products.map((p, idx) => (
                  <tr key={idx} style={{ borderTop: '1px solid #E5E7EB' }}>
                    <td style={{ padding: 8, color: '#6B7280' }}>#{idx + 1}</td>
                    <td style={{ padding: 8, fontWeight: 500 }}>{p.product_name}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{p.total_quantity}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{p.visits}</td>
                    <td style={{ padding: 8, textAlign: 'right', fontWeight: 600, color: '#166534' }}>
                      {fmtCurrency(p.total_spend)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Transaction history — each row is chevron-expandable to show line
          items pulled via the invoice_number → POSTransaction.transaction_number
          join. Invoice numbers link to the POS receipt detail page. */}
      <h2 style={{ marginBottom: 12 }}>Transaction history</h2>
      {visitor.transactions.length === 0 ? (
        <div style={{ color: '#6B7280' }}>No transactions on record.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {visitor.transactions.map((t) => {
            const isOpen = expandedTxns.has(t.id);
            const hasItems = t.items && t.items.length > 0;
            return (
              <div key={t.id} style={{
                background: '#FFFFFF',
                border: '1px solid #E5E7EB',
                borderRadius: 6,
                overflow: 'hidden',
              }}>
                <div
                  onClick={() => hasItems && toggleTxn(t.id)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '20px 110px 90px 70px 1fr 110px',
                    gap: 8,
                    alignItems: 'center',
                    padding: '8px 10px',
                    cursor: hasItems ? 'pointer' : 'default',
                    fontSize: 13,
                  }}
                >
                  <span style={{ color: '#6B7280' }}>
                    {hasItems ? (isOpen ? '▼' : '▶') : ' '}
                  </span>
                  <span>{t.date}</span>
                  <span style={{ fontWeight: 600 }}>${t.amount}</span>
                  <span style={{ color: '#6B7280' }}>{t.payment_method.toUpperCase()}</span>
                  <span>
                    {t.invoice_number ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/bridge-club/pos-receipt/${t.invoice_number}`);
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#2D5A27',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          fontFamily: 'monospace',
                          padding: 0,
                          fontSize: 12,
                        }}
                      >
                        {t.invoice_number}
                      </button>
                    ) : (
                      <span style={{ color: '#9CA3AF' }}>—</span>
                    )}
                    {t.cashier && (
                      <span style={{ color: '#6B7280', marginLeft: 6 }}>· {t.cashier}</span>
                    )}
                  </span>
                  <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#6B7280', textAlign: 'right' }}>
                    {t.helcim_transaction_id}
                  </span>
                </div>
                {isOpen && hasItems && (
                  <div style={{
                    borderTop: '1px solid #E5E7EB',
                    background: '#F9FAFB',
                    padding: '8px 10px 10px 38px',
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <tbody>
                        {t.items.map((it, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px dashed #E5E7EB' }}>
                            <td style={{ padding: '4px 0' }}>
                              {it.product_name}
                              {it.is_red_bridge && (
                                <span style={{
                                  marginLeft: 6,
                                  fontSize: 10,
                                  color: '#065F46',
                                  background: '#D1FAE5',
                                  borderRadius: 3,
                                  padding: '0 4px',
                                  fontWeight: 600,
                                }}>RB</span>
                              )}
                            </td>
                            <td style={{ padding: '4px 8px', color: '#6B7280', textAlign: 'right' }}>
                              {it.quantity}{it.is_weight_based ? ' lb' : ''}
                            </td>
                            <td style={{ padding: '4px 8px', color: '#6B7280', textAlign: 'right' }}>
                              @ {fmtCurrency(it.unit_price)}
                            </td>
                            <td style={{ padding: '4px 0', textAlign: 'right', fontWeight: 500 }}>
                              {fmtCurrency(it.line_total)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UnmatchedVisitorDetail;
